import { useCallback } from 'react'

interface ZoomControlsProps {
  totalCount: number
  visibleRange: { start: number; end: number }
  setVisibleRange: React.Dispatch<React.SetStateAction<{ start: number; end: number }>>
  minWindow: number
  maxWindow?: number
  zoomRatio: number
  panRatio: number
  onRequestExtend?: (options: { targetWindow: number }) => void
  showPanControls?: boolean
  invertPan?: boolean
  zoomOutPending?: boolean
}

function ZoomControls({
  totalCount,
  visibleRange,
  setVisibleRange,
  minWindow,
  maxWindow,
  zoomRatio,
  panRatio,
  onRequestExtend,
  showPanControls = true,
  invertPan = false,
  zoomOutPending = false,
}: ZoomControlsProps) {
  const windowLength = Math.max(1, visibleRange.end - visibleRange.start + 1)
  const effectiveMaxWindow = maxWindow ?? totalCount
  const canZoomIn = windowLength > minWindow
  const canZoomOut = windowLength < effectiveMaxWindow
  const canPanLeft = showPanControls && (invertPan ? visibleRange.end < totalCount - 1 : visibleRange.start > 0)
  const canPanRight = showPanControls && (invertPan ? visibleRange.start > 0 : visibleRange.end < totalCount - 1)
  const zoomOutDisabled = (!canZoomOut && !onRequestExtend) || zoomOutPending

  const clampRange = useCallback(
    (start: number, end: number) => {
      const maxIndex = totalCount - 1
      if (maxIndex < 0) return { start: 0, end: 0 }
      const clampedStart = Math.max(0, Math.min(start, maxIndex))
      const clampedEnd = Math.max(clampedStart, Math.min(end, maxIndex))
      return { start: clampedStart, end: clampedEnd }
    },
    [totalCount]
  )

  const applyRange = useCallback(
    (start: number, end: number) => {
      const next = clampRange(start, end)
      setVisibleRange((prev) => (prev.start === next.start && prev.end === next.end ? prev : next))
    },
    [clampRange, setVisibleRange]
  )

  const handleZoomIn = useCallback(() => {
    if (!canZoomIn) return
    const zoomStep = Math.max(1, Math.floor(windowLength * zoomRatio))
    const newLength = Math.max(minWindow, windowLength - zoomStep)
    const center = visibleRange.start + windowLength / 2
    const newStart = Math.round(center - newLength / 2)
    const newEnd = newStart + newLength - 1
    applyRange(newStart, newEnd)
  }, [applyRange, canZoomIn, windowLength, zoomRatio, minWindow, visibleRange])

  const handleZoomOut = useCallback(() => {
    if (!canZoomOut) {
      const zoomStep = Math.max(1, Math.floor(windowLength * zoomRatio))
      const desiredWindow = Math.min(totalCount + zoomStep, windowLength + zoomStep)
      onRequestExtend?.({ targetWindow: desiredWindow })
      return
    }
    const zoomStep = Math.max(1, Math.floor(windowLength * zoomRatio))
    const newLength = Math.min(effectiveMaxWindow, windowLength + zoomStep)
    const center = visibleRange.start + windowLength / 2
    const newStart = Math.round(center - newLength / 2)
    const newEnd = newStart + newLength - 1
    applyRange(newStart, newEnd)
  }, [applyRange, canZoomOut, windowLength, zoomRatio, effectiveMaxWindow, visibleRange, onRequestExtend])

  const handlePanLeft = useCallback(() => {
    if (!canPanLeft) return
    const step = Math.max(1, Math.floor(windowLength * panRatio))
    if (invertPan) {
      const newEnd = Math.min(totalCount - 1, visibleRange.end + step)
      const newStart = Math.max(0, newEnd - windowLength + 1)
      applyRange(newStart, newEnd)
    } else {
      const newStart = Math.max(0, visibleRange.start - step)
      const newEnd = Math.min(totalCount - 1, newStart + windowLength - 1)
      applyRange(newStart, newEnd)
    }
  }, [applyRange, canPanLeft, panRatio, visibleRange, windowLength, invertPan, totalCount])

  const handlePanRight = useCallback(() => {
    if (!canPanRight) return
    const step = Math.max(1, Math.floor(windowLength * panRatio))
    if (invertPan) {
      const newStart = Math.max(0, visibleRange.start - step)
      const newEnd = Math.min(totalCount - 1, newStart + windowLength - 1)
      applyRange(newStart, newEnd)
    } else {
      const newEnd = Math.min(totalCount - 1, visibleRange.end + step)
      const newStart = Math.max(0, newEnd - windowLength + 1)
      applyRange(newStart, newEnd)
    }
  }, [applyRange, canPanRight, panRatio, totalCount, visibleRange, windowLength, invertPan])

  const buttonClass =
    'px-3 py-1 text-xs rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="flex items-center gap-2">
      {showPanControls ? (
        <button
          type="button"
          aria-label="Scroll left"
          className={buttonClass}
          onClick={handlePanLeft}
          disabled={!canPanLeft}
        >
          ‹
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Zoom out"
        className={buttonClass}
        onClick={handleZoomOut}
        disabled={zoomOutDisabled}
      >
        -
      </button>
      <button type="button" aria-label="Zoom in" className={buttonClass} onClick={handleZoomIn} disabled={!canZoomIn}>
        +
      </button>
      {showPanControls ? (
        <button
          type="button"
          aria-label="Scroll right"
          className={buttonClass}
          onClick={handlePanRight}
          disabled={!canPanRight}
        >
          ›
        </button>
      ) : null}
    </div>
  )
}

export default ZoomControls
